import './argon-pro/scss/argon-dashboard.scss';
import "bootstrap-icons/font/bootstrap-icons.css";
import './htmx-transitions.css';
import * as bootstrap from 'bootstrap';
import htmx from "htmx.org";
import Alpine from 'alpinejs';
import Sortable from 'sortablejs';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import {Html5Qrcode, Html5QrcodeScanner} from "html5-qrcode";
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import Chart from 'chart.js/auto';
import JSONEditor from 'jsoneditor';
import 'jsoneditor/dist/jsoneditor.min.css';
window.JSONEditor = JSONEditor;

/* Ignore cross-origin requests */
htmx.config.selfRequestsOnly = true;
/* Disable View Transitions API */
htmx.config.globalViewTransitions = false;
/* On browser back/forward, do a full page reload if history cache misses */
htmx.config.refreshOnHistoryMiss = true;
/* Swap delay allows CSS fade-out transition to complete before content swap */
htmx.config.defaultSwapDelay = 150;

window.htmx = htmx;
window.bootstrap = bootstrap;
window.Swal = Swal;
window.Sortable = Sortable;
window.Html5Qrcode = Html5Qrcode;
window.Html5QrcodeScanner = Html5QrcodeScanner;
window.Alpine = Alpine;
window.Chart = Chart;

Alpine.data("autocomplete", () => ({
    selectedId: null,
    selectedName: "",
    query: "",
    clearSelection() {
        this.selectedId = null;
        this.query = "";
        const root = this.$root;
        this.$nextTick(() => {
            htmx.process(root);
        });
    },
    setSelected(name, id) {
        this.selectedName = name;
        this.selectedId = id;
        this.$nextTick(() => {
            this.$refs.resultsContent.remove();
        });
    },
    hasSelected() {
        return this.selectedId !== null;
    }
}));


/**
 * Alpine directive for QR code scanning with modal integration.
 * Vibecoded (needs to be tested)
 * Usage:
 *   <button x-data x-qr-scanner="{
 *     modalId: 'my-modal',
 *     readerId: 'qr-reader',
 *     siteId: 123,
 *     onSuccess: 'navigate|validate|create|custom',
 *     navigateTo: '/customer/{studentId}/',
 *     validateUrl: '/validate/{lessonId}/{qrCode}/',
 *     createUrl: '/create/{studentId}/',
 *     refreshUrl: '/refresh/',
 *     lessonId: 123
 *   }">
 */
Alpine.directive('qr-scanner', (el, {expression}, {evaluate}) => {
    const config = evaluate(expression);

    const decodeQR = (decodedResult) => {
        try {
            return atob(decodedResult['decodedText']).split('|');
        } catch (e) {
            return [null, null, decodedResult['decodedText']];
        }
    };

    const closeAndCleanup = (modal, scanner) => {
        if (scanner) {
            scanner.pause();
            scanner.clear().catch(() => {
            });
        }
        if (modal) {
            modal.close();
            modal._qrScanner = null;
            modal._qrScanned = false;
        }
    };

    const createSuccessHandler = (modal, getCurrentLessonId) => async (decodedText, decodedResult) => {
        if (modal._qrScanned) return;

        const [studentId, studentSite, rawCode] = decodeQR(decodedResult);
        const scanner = modal._qrScanner;

        // Site validation if siteId is provided
        if (config.siteId && studentSite && parseInt(studentSite) !== parseInt(config.siteId)) {
            alert('O código QR do aluno pertence a outra escola.');
            return;
        }

        modal._qrScanned = true;

        switch (config.onSuccess) {
            case 'navigate':
                closeAndCleanup(modal, scanner);
                const url = config.navigateTo.replace('{studentId}', studentId);
                setTimeout(() => htmx.ajax('GET', url, 'main'), 500);
                break;

            case 'validate':
                const lessonId = getCurrentLessonId();
                const validateUrl = config.validateUrl
                    .replace('{lessonId}', lessonId)
                    .replace('{qrCode}', decodedResult['decodedText']);
                try {
                    const response = await fetch(validateUrl, {
                        method: 'POST',
                        headers: {'X-CSRFToken': config.csrfToken},
                    });
                    if (response.ok) {
                        const data = await response.json();
                        if ('error' in data) {
                            alert(data['error']);
                            modal._qrScanned = false;
                        } else {
                            alert('Aula validada com sucesso.');
                            closeAndCleanup(modal, scanner);
                            if (config.refreshUrl) {
                                htmx.ajax('GET', config.refreshUrl, 'main');
                            }
                        }
                    }
                } catch (e) {
                    console.error('Validation error:', e);
                    modal._qrScanned = false;
                }
                break;

            case 'create':
                const createUrl = config.createUrl.replace('{studentId}', studentId);
                try {
                    const response = await fetch(createUrl, {
                        method: 'POST',
                        headers: {'X-CSRFToken': config.csrfToken},
                    });
                    const data = await response.json();
                    if (Object.keys(data).length === 0) {
                        alert('Aula criada com sucesso!');
                    } else {
                        alert('Ocorreu um erro durante a criação da aula');
                    }
                    closeAndCleanup(modal, scanner);
                    if (config.refreshUrl) {
                        htmx.ajax('GET', config.refreshUrl, 'main');
                    }
                } catch (e) {
                    console.error('Create error:', e);
                    modal._qrScanned = false;
                }
                break;

            case 'custom':
                // Dispatch custom event for external handling
                el.dispatchEvent(new CustomEvent('qr-scanned', {
                    detail: {studentId, studentSite, rawCode, decodedText, decodedResult},
                    bubbles: true
                }));
                closeAndCleanup(modal, scanner);
                break;
        }
    };

    const triggerScanner = async () => {
        const cameras = await Html5Qrcode.getCameras();
        if (cameras.length === 0) {
            alert('Não foram encontradas câmaras no sistema.');
            return;
        }

        const modal = document.getElementById(config.modalId);
        modal.showModal();
        modal._qrScanned = false;

        const scannerConfig = {
            fps: config.fps || 10,
            qrbox: config.qrbox || {width: 250, height: 250}
        };

        const scanner = new Html5QrcodeScanner(config.readerId, scannerConfig, false);
        modal._qrScanner = scanner;

        // For 'validate' mode, capture lessonId at trigger time
        const getCurrentLessonId = () => config.lessonId || el.getAttribute('data-lesson-id');

        scanner.render(createSuccessHandler(modal, getCurrentLessonId), () => {
        });
    };

    // Bind click event to trigger element
    el.addEventListener('click', triggerScanner);
});

/**
 * Alpine directive to pause a QR scanner when closing modal.
 * Usage: <button x-qr-scanner-close="modalId">X</button>
 */
Alpine.directive('qr-scanner-close', (el, {expression}) => {
    el.addEventListener('click', () => {
        const modal = document.getElementById(expression);
        if (modal && modal._qrScanner) {
            modal._qrScanner.pause();
        }
    });
});

Alpine.directive('editor', (el, {expression}, {evaluateLater, effect}) => {
    /**
     * Alpine directive to initialize block-based rich text editor.
     * The expression must include a serialized JSON with an *initial* key with the starting text.
     *
     */
    const generateBlockFromText = (text) => {
        return {
            type: 'paragraph',
            data: {
                text
            }
        }
    }
    const generateEditorConfiguration = (initialValue = '', fieldName) => {
        return {
            autofocus: true,
            tools: {
                header: {
                    class: Header,
                    inlineToolbar: true
                },
            },
            i18n: {
                messages: {
                    ui: {
                        "blockTunes": {
                            "toggler": {
                                "Click to tune": "Clique para ajustar",
                                "or drag to move": "ou arraste para mover"
                            },
                        },
                        "inlineToolbar": {
                            "converter": {
                                "Convert to": "Converter para"
                            }
                        },
                        "toolbar": {
                            "toolbox": {
                                "Add": "Adicionar"
                            }
                        }
                    },
                    toolNames: {
                        "Text": "Texto",
                        "Heading": "Título",
                        "List": "Lista",
                        "Warning": "Aviso",
                        "Checklist": "Checklist",
                        "Quote": "Citação",
                        "Code": "Código",
                        "Delimiter": "Delimitador",
                        "Raw HTML": "HTML Puro",
                        "Table": "Tabela",
                        "Link": "Link",
                        "Marker": "Marcador",
                        "Bold": "Negrito",
                        "Italic": "Itálico",
                        "InlineCode": "Código de linha",
                    },
                    blockTunes: {
                        "delete": {
                            "Delete": "Excluir"
                        },
                        "moveUp": {
                            "Move up": "Mover para cima"
                        },
                        "moveDown": {
                            "Move down": "Mover para baixo"
                        }
                    },
                }
            },
            data: {
                blocks: [
                    generateBlockFromText(initialValue)
                ]
            },
            onChange: async (api, event) => {
                const data = await api.saver.save();
                const textContent = data.blocks.map(block => block.data.text || '').join('\n');
                const input = document.getElementsByName(fieldName)[0];
                input.value = textContent;
                input.dispatchEvent(new Event('change'));
            }
        }
    }

    const initializeComponent = () => {
        const holder = document.createElement('div');
        holder.id = "editorjs";
        holder.style.maxHeight = "500px";
        holder.style.overflowY = "auto";
        el.parentNode.insertBefore(holder, el.nextSibling);
        el.style.display = 'none';
        const initialData = JSON.parse(expression);
        const initialValue = initialData?.initial || '';
        const fieldName = initialData?.fieldName;
        // Set initial value to the hidden input field
        const input = document.getElementsByName(fieldName)[0];
        if (input) {
            input.value = initialValue;
        }
        window.NTR_CURRENT_EDITOR_JS = new EditorJS(generateEditorConfiguration(initialValue, fieldName));
    }
    initializeComponent();
});

/**
 * Alpine directive to initialize Bootstrap popovers.
 * Searches the element's subtree for [data-bs-toggle="popover"] triggers.
 * Modifiers:
 *   .cleanup - also removes stale .popover elements from the DOM first
 *   .global  - search the entire document instead of just the element subtree
 * Usage:
 *   <div x-popover-init>...</div>
 *   <div x-popover-init.cleanup>...</div>
 *   <aside x-popover-init.global>...</aside>
 */
Alpine.directive('popover-init', (el, {modifiers}) => {
    if (modifiers.includes('cleanup')) {
        document.querySelectorAll('.popover').forEach(p => p.remove());
    }
    const scope = modifiers.includes('global') ? document : el;
    // Below Bootstrap's md breakpoint the grids print observations inline
    // (.ntr-obs-note), because a touch on the name follows its link rather than
    // opening a hover popover. Skip those triggers so the two never both fire.
    const isPhone = window.matchMedia('(max-width: 767.98px)').matches;
    scope.querySelectorAll('[data-bs-toggle="popover"]').forEach(trigger => {
        if (isPhone && trigger.classList.contains('ntr-obs-popover')) {
            return;
        }
        new bootstrap.Popover(trigger);
    });
});

/**
 * Alpine directive to remove stale Bootstrap popover elements from the DOM.
 * Usage: <section x-popover-cleanup>...</section>
 */
Alpine.directive('popover-cleanup', () => {
    document.querySelectorAll('.popover').forEach(el => el.remove());
});

/**
 * Alpine directive to show a SweetAlert2 dialog on element init.
 * Usage: <div x-swal="{ icon: 'warning', title: 'Alert', text: 'Message' }"></div>
 */
Alpine.directive('swal', (el, {expression}, {evaluate}) => {
    Swal.fire(evaluate(expression));
});

/**
 * Alpine directive that shows a SweetAlert2 confirmation before letting a
 * click proceed. It runs in the capture phase and stops the event, so any
 * other click handlers on the element (e.g. x-qz-print) are suppressed until
 * the user confirms; on confirm the click is replayed so those handlers fire.
 * Usage: <button x-data x-confirm="{ title: 'Atenção', html: '...' }">...</button>
 */
Alpine.directive('confirm', (el, {expression}, {evaluate}) => {
    let confirmed = false;
    el.addEventListener('click', (event) => {
        if (confirmed) return;
        event.stopImmediatePropagation();
        Swal.fire({
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#5e72e4',
            confirmButtonText: '<i class="bi bi-check-lg me-2"></i>Continuar',
            cancelButtonText: '<i class="bi bi-x-lg me-2"></i>Cancelar',
            ...evaluate(expression),
        }).then((result) => {
            if (result.isConfirmed) {
                confirmed = true;
                el.click();
                confirmed = false;
            }
        });
    }, true);
});

/**
 * Shared QZ Tray print helper.
 * Fetches ticket data from a URL and prints it via QZ Tray.
 * @param {string} url - The URL to fetch ticket data from (POST).
 * @param {string} printerName - The OS printer name.
 * @param {string} csrfToken - CSRF token for the POST request.
 * @param {Object} [options] - Optional settings.
 * @param {string} [options.body] - Custom POST body.
 * @returns {Promise<Object>} The response data from the fetch.
 */
async function qzPrint(url, printerName, csrfToken, options = {}) {
    const fetchOptions = {
        method: 'POST',
        headers: {'X-CSRFToken': csrfToken},
    };
    if (options.body) fetchOptions.body = options.body;
    const response = await fetch(url, fetchOptions);
    const data = await response.json();
    // The server may reject the request (e.g. an account already exists for this
    // email). Surface the message and skip printing instead of sending an empty
    // receipt to the printer.
    if (!response.ok) {
        window.alert(data.error || 'Ocorreu um erro ao processar o pedido.');
        return data;
    }
    await qz.websocket.connect();
    try {
        const config = qz.configs.create(printerName);
        await qz.print(config, [[{
            type: 'pixel',
            format: 'html',
            flavor: 'plain',
            data: data.raw_data,
        }]]);
    } catch (e) {
        window.alert('Ocorreu um erro na comunicação com a impressora!\nErro: ' + e);
    } finally {
        await qz.websocket.disconnect();
    }
    return data;
}

/**
 * Alpine directive for QZ Tray ticket printing.
 * Usage:
 *   Simple: <a x-data x-qz-print="{ url: '/ticket/', printer: 'PRINTER', csrf: 'token' }">Print</a>
 *   With checked transactions: add checkSelector: '.transaction_check'
 *   With callback: add onSuccess: (data) => { ... }
 */
Alpine.directive('qz-print', (el, {expression}, {evaluate}) => {
    el.addEventListener('click', async () => {
        const config = evaluate(expression);
        let body = null;
        if (config.checkSelector) {
            const values = Array.from(document.querySelectorAll(config.checkSelector))
                .filter(el => el.checked).map(el => el.value);
            if (values.length === 0) {
                window.alert('Não selecionou nenhuma transação para impressão.');
                return;
            }
            body = JSON.stringify(values);
        }
        const data = await qzPrint(config.url, config.printer, config.csrf, {body});
        if (config.onSuccess) config.onSuccess(data);
    });
});

/**
 * Alpine directive for sending an SMS through the iris gateway before
 * letting an HTMX form submit hit the Django server.
 *
 * The gateway runs on the user's LAN (the Windows machine driving the
 * M7010 router) and is unreachable from the Django host, so the request
 * must originate from the browser. We hook htmx:confirm to defer the
 * htmx POST until iris confirms `ok: true`; the server-side form submit
 * is then responsible for the audit log only.
 *
 * Usage: <form x-data x-sms-gateway-form="{ gatewayUrl: 'http://...', gatewayToken: '...' }" hx-post="...">
 */
Alpine.directive('sms-gateway-form', (el, {expression}, {evaluate}) => {
    el.addEventListener('htmx:confirm', async (event) => {
        if (event.target !== el) return;
        event.preventDefault();
        const config = evaluate(expression);
        const gatewayUrl = (config.gatewayUrl || '').replace(/\/$/, '');
        if (!gatewayUrl) {
            window.alert('Gateway de SMS não configurado.');
            return;
        }
        const phone = el.querySelector('[name="phone_number"]').value.trim();
        const message = el.querySelector('[name="sms_message"]').value;
        const headers = {'Content-Type': 'application/json'};
        if (config.gatewayToken) headers['Authorization'] = `Bearer ${config.gatewayToken}`;
        try {
            const resp = await fetch(`${gatewayUrl}/sms`, {
                method: 'POST',
                headers,
                body: JSON.stringify({to: phone, text: message}),
            });
            if (!resp.ok) {
                const body = await resp.text();
                window.alert(`Falha no envio de SMS (${resp.status}):\n${body}`);
                return;
            }
            const data = await resp.json();
            if (!data.ok) {
                window.alert(`Falha no envio de SMS: ${JSON.stringify(data.raw || data)}`);
                return;
            }
            event.detail.issueRequest();
        } catch (e) {
            window.alert(`Erro ao contactar o gateway SMS:\n${e.message}`);
        }
    });
});

/**
 * Alpine.data factory for the SMS inbox/outbox page.
 *
 * Fetches messages from the iris gateway and renders them client-side.
 * Delete proxies through the gateway, then POSTs to the Django audit
 * endpoint so the deletion shows up in the LogEntry stream.
 *
 * Usage: <section x-data="smsBox({ gatewayUrl, gatewayToken, box, logDeleteUrl, csrf, pollMs })">
 */
Alpine.data('smsBox', (cfg) => ({
    cfg,
    messages: [],
    total: 0,
    loading: false,
    error: null,
    _pollHandle: null,
    init() {
        // When the gateway isn't configured, the server renders a warning
        // badge at the top of the page; the list stays empty and silent.
        if (!this.cfg.gatewayUrl) return;
        this.cfg.gatewayUrl = this.cfg.gatewayUrl.replace(/\/$/, '');
        this.refresh();
        if (this.cfg.pollMs > 0) {
            this._pollHandle = setInterval(() => this.refresh(), this.cfg.pollMs);
        }
    },
    destroy() {
        if (this._pollHandle) clearInterval(this._pollHandle);
    },
    _headers() {
        const h = {'Content-Type': 'application/json'};
        if (this.cfg.gatewayToken) h['Authorization'] = `Bearer ${this.cfg.gatewayToken}`;
        return h;
    },
    async refresh() {
        this.loading = true;
        this.error = null;
        try {
            const resp = await fetch(`${this.cfg.gatewayUrl}/sms/${this.cfg.box}?page=1&amount=100`, {
                headers: this._headers(),
            });
            if (!resp.ok) {
                this.error = `Falha ao obter mensagens (${resp.status}).`;
                return;
            }
            const data = await resp.json();
            this.messages = data.messages || [];
            this.total = data.total || 0;
        } catch (e) {
            this.error = `Erro ao contactar o gateway: ${e.message}`;
        } finally {
            this.loading = false;
        }
    },
    async remove(message) {
        if (!window.confirm('Remover esta mensagem do gateway?')) return;
        message._deleting = true;
        try {
            const resp = await fetch(`${this.cfg.gatewayUrl}/sms/${this.cfg.box}`, {
                method: 'DELETE',
                headers: this._headers(),
                body: JSON.stringify({indices: [message.index]}),
            });
            if (!resp.ok) {
                message._deleting = false;
                this.error = `Falha ao remover (${resp.status}).`;
                return;
            }
            // Fire-and-forget audit log; failure here shouldn't block the UX.
            fetch(this.cfg.logDeleteUrl, {
                method: 'POST',
                headers: {'Content-Type': 'application/json', 'X-CSRFToken': this.cfg.csrf},
                body: JSON.stringify({index: message.index, content: message.content}),
            }).catch(() => {});
            this.messages = this.messages.filter(m => m.index !== message.index);
            this.total = Math.max(0, this.total - 1);
        } catch (e) {
            message._deleting = false;
            this.error = `Erro ao remover: ${e.message}`;
        }
    },
}));

/**
 * Alpine directive to initialize a JSONEditor on a container element.
 * Expression must be a JSON string with containerId and textareaId.
 * Usage: <div x-data x-json-editor="{ containerId: 'id_field', textareaId: 'id_field_textarea' }"></div>
 */
Alpine.directive('json-editor', (el, {expression}, {evaluate, cleanup}) => {
    const config = evaluate(expression);
    const container = document.getElementById(config.containerId);
    const textarea = document.getElementById(config.textareaId);
    if (!container || !textarea) return;
    textarea.removeAttribute('required');
    if (container.dataset.editorInitialized) {
        container.innerHTML = '';
        delete container.dataset.editorInitialized;
    }
    let initial = config.initial || {};
    if (config.dataId) {
        const dataEl = document.getElementById(config.dataId);
        if (dataEl) {
            try { initial = JSON.parse(dataEl.textContent); } catch(e) {}
        }
    }
    const editor = new JSONEditor(container, {
        modes: ['text', 'code', 'tree', 'form', 'view'],
        mode: 'form',
        search: true,
        onChange() {
            textarea.value = JSON.stringify(editor.get());
        }
    });
    editor.set(initial);
    textarea.value = JSON.stringify(initial);
    container.dataset.editorInitialized = true;
    cleanup(() => {
        editor.destroy();
    });
});

Alpine.start();
